<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { IconDown } from '../icon'
import { DropdownOption } from '@caks/shared/types/types.ui-kit'

interface Props {
  options: DropdownOption[] | string[]
  disabled?: boolean
  modelValue: DropdownOption | null | string | undefined
  placeholder?: string
  label?: string
  nameKey?: string
}
interface Emit {
  (emit: 'update:modelValue', value: DropdownOption): void
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Выберите',
  nameKey: 'name',
})
const emit = defineEmits<Emit>()

const isOpen = ref(false)

function selectOption(item: DropdownOption) {
  emit('update:modelValue', item)
  isOpen.value = false
}

const buttonClasses = computed(() => {
  return {
    dropdown__button_selected: props.modelValue,
    dropdown__button_opened: isOpen.value,
  }
})

const openDropDown = () => {
  if (props.disabled) return

  isOpen.value = !isOpen.value
}

const closeOptions = () => (isOpen.value = false)

onMounted(() => document.addEventListener('click', closeOptions))
onUnmounted(() => document.removeEventListener('click', closeOptions))
</script>

<template>
  <div class="wrapper" :class="{ 'wrapper_with-label': label }" @click.stop>
    <div class="label">
      <label v-if="label">{{ label }}</label>
      <div class="dropdown" :class="{ dropdown_opened: isOpen }">
        <div class="dropdown__button" :class="buttonClasses" @click="openDropDown">
          <slot name="left-icon" />
          <span>{{ modelValue?.[nameKey] ?? placeholder }}</span>
          <icon-wrapper color="#242933">
            <icon-down></icon-down>
          </icon-wrapper>
        </div>
        <transition>
          <ul v-if="isOpen" class="dropdown__options options">
            <li
              v-for="item in options"
              :key="item.id"
              class="options__item"
              @click.stop="selectOption(item)"
            >
              <p>{{ item[nameKey] }}</p>
            </li>
          </ul>
        </transition>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  position: relative;
  height: 2.75rem;

  &_with-label {
    height: 4.5rem;
  }
}
.label {
  @include h3-5;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.dropdown {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  cursor: pointer;
  transition: box-shadow 0.15s ease-in-out;
  border-radius: 0.313rem;
  overflow: hidden;

  &_opened {
    box-shadow: 0 0 5px #00000026;
  }

  &__button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    border: 2px solid #f5f5f8;
    border-radius: 0.5rem;
    background-color: $white;
    padding: 0.5rem 1rem;
    @include h5-2;
    transition: border 0.1s ease-in-out;
    color: $primary-text;

    @media (hover: hover) {
      &:hover {
        border-color: $hover-green;
      }
    }

    svg {
      transition: transform 0.15s ease-in-out;
    }

    &_selected {
      color: $primary-text;
    }

    &_opened {
      border-color: #fafafa;
      svg {
        transform: rotate(180deg);
      }
      &:hover {
        border-color: #fafafa;
      }
    }
  }
}

.options {
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  width: 100%;
  max-height: 9.5rem;
  background-color: $ui-gray-2;
  z-index: 1;

  &__item {
    display: flex;
    gap: 1.5rem;
    width: 100%;
    padding: 0.75rem 1rem;
    background-color: $white;
    @include h5-2;

    .flag {
      min-width: 1rem;
      max-width: 1rem;
    }

    &:hover {
      color: $hover-green;
    }
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: $ui-gray-2;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: $ui-gray-3;
    border-radius: 10px;
    border: 2px solid $ui-gray-2;
  }
}

.v-enter-active,
.v-leave-active {
  transition: all 0.15s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  transform: translateY(-1rem);
  opacity: 0;
}
</style>
